import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react'
import cx from 'classnames'
import { useMenuItem } from '@react-aria/menu'
import { FocusRing } from '@react-aria/focus'
import Styles from './Menu.module.scss'
import { MenuItemProps } from './Types'
import { Item as ReactStatelyItem } from '@react-stately/collections'
import { ItemProps as ReactStatelyItemProps } from '@react-types/shared'
import { Check2, CheckboxChecked } from 'blaze-ui/icons'
import { Icon } from 'almanac-ui/icons/Types'
import { Text } from 'blaze-ui/Text/Text'

interface MenuItemContentProps {
  frontIcon?: Icon
  endIcon?: Icon
  description?: ReactNode
}

interface ItemProps<T> extends MenuItemContentProps, ReactStatelyItemProps<T> {}

interface SelectableItemProps<T> extends ItemProps<T> {
  type: 'checkbox' | 'item'
  closeOnSelect?: boolean
}

export const Item: <T>(props: ItemProps<T>) => JSX.Element = ReactStatelyItem
export const SelectableItem: <T>(props: SelectableItemProps<T>) => JSX.Element = ReactStatelyItem

export const MenuItemContent = ({
  frontIcon: FrontIcon,
  endIcon: EndIcon,
  description,
  children,
}: PropsWithChildren<MenuItemContentProps>) => {
  // Not using the useIsSystemRedesign hook because it would cause lots of tests that are not wrapped in redux Providers and that render MenuItems to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'
  const iconSize = isSystemRedesignEnabled ? 16 : 20

  return (
    <div className={Styles.itemContent}>
      {FrontIcon && (
        <div className={Styles.frontIcon}>
          <FrontIcon size={iconSize} />
        </div>
      )}
      <div className={Styles.itemLabelContainer}>
        <div className={Styles.itemLabelContent}>
          {children}
          {EndIcon && <EndIcon size={iconSize} color="var(--dark-40)" />}
        </div>
        {description && (
          <div className={Styles.description}>
            <Text variant="placeholder">{description}</Text>
          </div>
        )}
      </div>
    </div>
  )
}

function MenuItem<T>({
  className,
  item,
  showCheckmark = true,
  state,
  onAction,
  onClose,
  closeOnSelect: MenuItemCloseOnSelect = true,
  menuType = 'dropdown',
  showCheckbox = false,
}: MenuItemProps<T>) {
  const { rendered, key } = item
  const { closeOnSelect: itemCloseOnSelect, frontIcon: FrontIcon, endIcon: EndIcon, description } = item.props
  let { type } = item.props
  if (showCheckbox) {
    type = 'checkbox'
  }
  const closeOnSelect = itemCloseOnSelect ?? MenuItemCloseOnSelect

  const isExpanded = state.expandedKeys.has(key)
  const isSelected = state.selectionManager.isSelected(key.toString())
  const isDisabled = state.disabledKeys.has(key.toString())
  const isFocused = state.selectionManager.focusedKey === key

  // Not using the useIsSystemRedesign hook because it would cause lots of tests that are not wrapped in redux Providers and that render menu items to fail
  const isSystemRedesignEnabled = document.documentElement.getAttribute('data-autopilot-system-redesign') === 'on'

  const ref = useRef<HTMLLIElement | null>(null)

  // Patch to solve known issue: https://github.com/adobe/react-spectrum/issues/1513#issuecomment-1641830053
  useEffect(() => {
    const handleTouch = (event: TouchEvent) => {
      event.preventDefault()
    }
    const refElement = ref.current

    if (!!refElement) {
      refElement.addEventListener(
        'touchend',
        (e) => {
          if (
            (e.currentTarget as HTMLElement)?.getAttribute('type') !== 'submit' &&
            typeof (e.currentTarget as HTMLElement)?.getAttribute('href') !== 'string'
          ) {
            e.preventDefault()
          }
        },
        { passive: false },
      )
    }

    return () => {
      if (!!refElement) {
        refElement.removeEventListener('touchend', handleTouch)
      }
    }
  }, [])

  const { menuItemProps } = useMenuItem(
    {
      key,
      isDisabled,
      onAction,
      isSelected,
      onClose,
      closeOnSelect,
    },
    state,
    ref,
  )

  return (
    <FocusRing focusRingClass={Styles.itemHovered}>
      <li
        {...menuItemProps}
        ref={ref}
        className={cx(Styles.item, className, Styles[type], Styles[menuType], {
          [Styles.itemHovered]: isFocused || isExpanded,
          [Styles.itemSelected]: isSelected,
          [Styles.itemDisabled]: isDisabled,
        })}
      >
        {type === 'checkbox' && (
          <span className={Styles.itemCheckbox}>{isSelected && <CheckboxChecked color="var(--selected-fill)" />}</span>
        )}
        {(FrontIcon || EndIcon || description || menuType === 'tray') && (
          <MenuItemContent frontIcon={FrontIcon} endIcon={EndIcon} description={description}>
            <div className={Styles.itemLabel}>{rendered}</div>
            {type !== 'checkbox' && isSelected && showCheckmark && (
              <div className={Styles.check}>
                <Check2 size={isSystemRedesignEnabled ? 10 : 16} />
              </div>
            )}
          </MenuItemContent>
        )}
        {!FrontIcon && !EndIcon && !description && menuType !== 'tray' && (
          <div className={Styles.itemContent}>{rendered}</div>
        )}
        {!FrontIcon &&
          !EndIcon &&
          !description &&
          menuType !== 'tray' &&
          type !== 'checkbox' &&
          isSelected &&
          showCheckmark && (
            <>
              {isSystemRedesignEnabled ? (
                <div className={Styles.checkIcon}>
                  <Check2 size={10} />
                </div>
              ) : (
                <Check2 />
              )}
            </>
          )}
      </li>
    </FocusRing>
  )
}

export { MenuItem }
